import { createEffect, onCleanup } from 'solid-js';
import { useVenue } from './VenueProvider';

const CLOUD_REFRESH_MS = 30_000;
const HIDDEN_GRACE_MS = 10_000;

/**
 * Hold the lease that makes the controller publish the room's tempo and input levels.
 *
 * The Pi publishes that measurement only while somebody is looking at it, because Realtime Database bills
 * for bytes delivered and a room nobody is watching has to cost nothing. So the lease is taken while the
 * Music screen is MOUNTED and the tab is VISIBLE, refreshed every 30 s against the controller's 45 s
 * clamp, and released the moment either stops being true. Leaving a console open on another screen, or
 * minimising the window, stops the publishing without anybody having to decide to stop it.
 *
 * A hidden tab gets a ten-second grace before the release, the same as the output preview's: switching to
 * another tab to read something and coming straight back should not tear the lease down and build it up
 * again. A hidden tab never REFRESHES, though — if it stays hidden the lease simply lapses.
 *
 * Direct transports (LAN, Bluetooth) need none of this: the daemon already pushes its audio status to a
 * connected console, so the hook does nothing at all unless control is going through the cloud.
 */
export function useMusicTelemetry(): void {
  const venue = useVenue();
  let leasedDeviceId = '';
  let refreshTimer: number | undefined;
  let hiddenTimer: number | undefined;

  function clearTimers(): void {
    if (refreshTimer != null) window.clearInterval(refreshTimer);
    if (hiddenTimer != null) window.clearTimeout(hiddenTimer);
    refreshTimer = hiddenTimer = undefined;
  }

  function release(): void {
    const held = leasedDeviceId;
    leasedDeviceId = '';
    if (refreshTimer != null) window.clearInterval(refreshTimer);
    refreshTimer = undefined;
    if (!held || !venue.user() || !venue.cloudReachable()) return;
    void venue.cloudCall('subscribeMusicTelemetry', { deviceId: held, release: true }).catch(() => undefined);
  }

  function take(deviceId: string): void {
    if (leasedDeviceId === deviceId) return;
    release();
    leasedDeviceId = deviceId;
    const refresh = (): void => {
      if (leasedDeviceId !== deviceId) return;
      void venue.cloudCall('subscribeMusicTelemetry', { deviceId }).catch(() => undefined);
    };
    refresh();
    refreshTimer = window.setInterval(refresh, CLOUD_REFRESH_MS);
  }

  /**
   * Whether this console should be holding the lease at all, recomputed from the facts it depends on.
   *
   * `visible` is a signal rather than a read of `document` so the effect re-runs when the tab is hidden or
   * shown; everything else is already reactive.
   */
  createEffect(() => {
    const wanted = venue.controlTransport() === 'cloud'
      && venue.cloudReachable()
      && Boolean(venue.user())
      && venue.selectedDeviceId();
    if (!wanted) {
      release();
      return;
    }
    if (document.visibilityState === 'hidden') return;
    take(wanted);
  });

  function visibilityChanged(): void {
    if (document.visibilityState === 'hidden') {
      if (hiddenTimer != null) window.clearTimeout(hiddenTimer);
      hiddenTimer = window.setTimeout(() => {
        if (document.visibilityState === 'hidden') release();
      }, HIDDEN_GRACE_MS);
      return;
    }
    if (hiddenTimer != null) window.clearTimeout(hiddenTimer);
    hiddenTimer = undefined;
    const deviceId = venue.selectedDeviceId();
    if (deviceId && venue.controlTransport() === 'cloud' && venue.cloudReachable() && venue.user()) {
      take(deviceId);
    }
  }

  document.addEventListener('visibilitychange', visibilityChanged);
  onCleanup(() => {
    document.removeEventListener('visibilitychange', visibilityChanged);
    clearTimers();
    release();
  });
}
